/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Booking-card style search (white card: Start/End Date, Car Category, City,
 * "View Vehicles" button). The city field is a smart search (substring suggestions +
 * Claude resolve of landmarks/airports + voice) — see CitySearchInput.
 */

import { useEffect, useRef, useState } from 'react';
import { Calendar, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CarSearchRequest, StorefrontLocation } from '../types';
import api from '../services/api';
import CitySearchInput, { CitySearchHandle } from './CitySearchInput';

interface Props {
  onSearch: (req: CarSearchRequest) => void;
  /** Distinct category names derived from the loaded fleet (parent-owned filter). */
  categories: string[];
  category: string;
  onCategoryChange: (c: string) => void;
  loading?: boolean;
}

function todayISO(offsetDays = 0): string {
  return new Date(Date.now() + offsetDays * 864e5).toISOString().slice(0, 10);
}

function buildRequest(startDate: string, endDate: string, city: string, commonLocationId?: string): CarSearchRequest {
  return {
    city: city || undefined,
    commonLocationId: commonLocationId || undefined,
    // No geo filter — browse the whole catalogue (city matches owner/car city server-side).
    latitude: 0,
    longitude: 0,
    dateFrom: new Date(startDate || todayISO()).toISOString(),
    dateTo: new Date(endDate || todayISO(7)).toISOString(),
    rentalType: 0, // not filtering by rental-type bitmask
    distance: 0,
    typesIds: [],
  };
}

export default function SearchBar({ onSearch, categories, category, onCategoryChange, loading }: Props) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO(7));
  const [cities, setCities] = useState<string[]>([]);
  const [locations, setLocations] = useState<StorefrontLocation[]>([]);
  const [locationId, setLocationId] = useState('');
  const cityRef = useRef<CitySearchHandle>(null);

  useEffect(() => {
    api.getCities().then(setCities).catch(() => setCities([]));
    api.getLocations().then(setLocations).catch(() => setLocations([]));
  }, []);

  // "View Vehicles" / Enter → let the city field resolve (smart/voice) then run the search.
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    cityRef.current?.submit();
  };

  return (
    <form onSubmit={submit} className="booking-card bg-white rounded-lg shadow-2xl p-6 lg:p-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('search.title')}</h2>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-base font-bold text-gray-800 mb-2">{t('search.startDate')}</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={startDate}
              min={todayISO()}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
          </div>
        </div>
        <div>
          <label className="block text-base font-bold text-gray-800 mb-2">{t('search.endDate')}</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-base font-bold text-gray-800 mb-2">{t('search.category')}</label>
        <select
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
        >
          <option value="">{t('search.selectCategory')}</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {locations.length > 0 && (
        <div className="mb-4">
          <label className="block text-base font-bold text-gray-800 mb-2">{t('search.location', 'Pickup location')}</label>
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            <option value="">{t('search.anyLocation', 'Any location')}</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}{l.carCount ? ` (${l.carCount})` : ''}</option>
            ))}
          </select>
        </div>
      )}

      <div className="mb-6">
        <label className="block text-base font-bold text-gray-800 mb-2">{t('search.city')}</label>
        <CitySearchInput
          ref={cityRef}
          cities={cities}
          onSearch={(c) => onSearch(buildRequest(startDate, endDate, c, locationId))}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full py-4 px-6 flex items-center justify-center"
      >
        {loading ? t('search.searching') : t('search.viewVehicles')} <ArrowRight className="ml-2 h-5 w-5" />
      </button>
    </form>
  );
}
